import {
  Box,
  chakra,
  Flex,
  HStack,
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Tag,
  TagLabel,
  Text,
  useToast,
  VisuallyHidden,
  type SystemStyleObject,
} from "@chakra-ui/react";
import type { Content } from "@tiptap/react";
import { Editor, useEditorState } from "@tiptap/react";
import { components, type GroupBase, OptionProps, Select, type SelectInstance } from "chakra-react-select";
import { t } from "i18next";
import { forwardRef, useCallback, useId, useImperativeHandle, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import {
  FaFileImport,
  FaHighlighter,
  FaImage,
  FaLink,
  FaListOl,
  FaListUl,
  FaMagic,
  FaRedo,
  FaTable,
  FaUndo,
} from "react-icons/fa";
import { TextDocumentDto, useListDocumentTemplatesQuery, AiFeature } from "../../../autogen/bff-api";
import { RICH_TEXT_HIGHLIGHT_COLORS, RICH_TEXT_TEXT_COLORS } from "../../../config";
import { ColorPalette, type Swatch } from "./ColorPalette";
import { useSub } from "../../subscription/useSub";
import { FiMessageSquare } from "react-icons/fi";
import { useAskFurther } from "../../agent/useAskFurther";
import { extractImageUploadIds, withoutImageNodes } from "./imageNodes";
import { parseTiptapContent, TipTapCopyTemplateDocumentsHandler } from "./TipTap";
import { ToolbarButton } from "./ToolbarButton";
import { useRovingToolbar } from "./useRovingToolbar";
import { useSelectScreenReaderMessages } from "../Selector/useSelectScreenReaderMessages";

const textColorSwatches = (): Swatch[] => [
  { name: t("Black"), value: RICH_TEXT_TEXT_COLORS.black },
  { name: t("Grey"), value: RICH_TEXT_TEXT_COLORS.grey },
  { name: t("Red"), value: RICH_TEXT_TEXT_COLORS.red },
  { name: t("Orange"), value: RICH_TEXT_TEXT_COLORS.orange },
  { name: t("Green"), value: RICH_TEXT_TEXT_COLORS.green },
  { name: t("Blue"), value: RICH_TEXT_TEXT_COLORS.blue },
  { name: t("Purple"), value: RICH_TEXT_TEXT_COLORS.purple },
];

const highlightColorSwatches = (): Swatch[] => [
  { name: t("Yellow"), value: RICH_TEXT_HIGHLIGHT_COLORS.yellow },
  { name: t("Green"), value: RICH_TEXT_HIGHLIGHT_COLORS.green },
  { name: t("Blue"), value: RICH_TEXT_HIGHLIGHT_COLORS.blue },
  { name: t("Pink"), value: RICH_TEXT_HIGHLIGHT_COLORS.pink },
  { name: t("Grey"), value: RICH_TEXT_HIGHLIGHT_COLORS.grey },
];

const replaceUploadIdsAndSrcInContent = ({
  content,
  uploadIdMapping,
  srcMapping,
}: {
  content: unknown;
  uploadIdMapping: Record<string, string>;
  srcMapping: Record<string, string>;
}): unknown => {
  if (!content || typeof content !== "object") return content;

  const node = content as Record<string, unknown>;

  if (node.type === "image" && node.attrs && typeof node.attrs === "object") {
    const attrs = node.attrs as Record<string, unknown>;
    const oldUploadId = attrs.uploadId as string | undefined;
    if (oldUploadId) {
      const newAttrs = { ...attrs };
      if (uploadIdMapping[oldUploadId]) {
        newAttrs.uploadId = uploadIdMapping[oldUploadId];
      }
      if (srcMapping[oldUploadId]) {
        newAttrs.src = srcMapping[oldUploadId];
      }
      return { ...node, attrs: newAttrs };
    }
  }

  if (Array.isArray(node.content)) {
    return {
      ...node,
      content: node.content.map((child) =>
        replaceUploadIdsAndSrcInContent({ content: child, uploadIdMapping, srcMapping })
      ),
    };
  }

  return node;
};

const isPlainParagraph = (editor: Editor) =>
  editor.isActive("paragraph") &&
  !["bulletList", "orderedList", "blockquote"].some((wrapper) => editor.isActive(wrapper));

export interface ToolbarConfig {
  undoRedo?: boolean;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  headings?: boolean;
  colors?: boolean;
  lists?: boolean;
  link?: boolean;
  table?: boolean;
  image?: boolean;
  template?: boolean;
  autofill?: boolean;
}

/** Lets the editor hand focus to the toolbar — see the Alt+F10 binding in `TipTap.tsx`. */
export type MenuBarHandle = { focus: () => void };

export type AskFurtherAbout = { source: string; subject?: string };

export const MenuBar = forwardRef<
  MenuBarHandle,
  {
    editor?: Editor;
    onImageSelect?: (files: File[]) => void;
    onCopyTemplateDocuments?: TipTapCopyTemplateDocumentsHandler;
    onAutofill?: () => void;
    isOpeningAutofill?: boolean;
    onTemplateInserted?: () => void;
    askFurtherAbout?: AskFurtherAbout;
    toolbar?: ToolbarConfig;
  }
>(({ editor, onImageSelect, onCopyTemplateDocuments, onAutofill, isOpeningAutofill, onTemplateInserted, askFurtherAbout, toolbar }, ref) => {
  const toolbarConfig: Required<ToolbarConfig> = {
    undoRedo: true, bold: true, italic: true, underline: true,
    headings: true, colors: true, lists: true,
    link: true, table: true, image: true, template: true, autofill: true,
    ...toolbar,
  };

  const toast = useToast();

  const sub = useSub();
  const askFurther = useAskFurther();

  const {
    data: documentTemplatesData,
    isLoading,
    isError: templatesFailed,
    refetch: refetchTemplates,
  } = useListDocumentTemplatesQuery(undefined, {
    skip: !toolbarConfig.template,
  });

  const imageInputRef = useRef<HTMLInputElement>(null);

  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [isInsertingTemplate, setIsInsertingTemplate] = useState(false);
  const insertingTemplateRef = useRef(false);

  const triggerImageInput = () => {
    imageInputRef.current?.click();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && onImageSelect) {
      onImageSelect(Array.from(files));
    }
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  const handleTemplateSelect = useCallback(
    async (template: TextDocumentDto) => {
      if (!editor) return;

      const content = parseTiptapContent(template.text);
      const hasAttachments = template.documents.length > 0 || extractImageUploadIds(content).length > 0;

      if (hasAttachments && onCopyTemplateDocuments) {
        if (insertingTemplateRef.current) return;
        insertingTemplateRef.current = true;
        setIsInsertingTemplate(true);
        try {
          const { uploadIdMapping, srcMapping } = await onCopyTemplateDocuments({
            templateDocuments: template.documents,
          });
          const updatedContent = replaceUploadIdsAndSrcInContent({ content, uploadIdMapping, srcMapping }) as Content;
          editor.commands.insertContent(updatedContent);
        } catch (error) {
          toast({
            variant: "subtle",
            status: "error",
            title: t("Failed to insert template"),
            description: t("Could not copy template documents"),
          });
          return;
        } finally {
          insertingTemplateRef.current = false;
          setIsInsertingTemplate(false);
        }
      } else if (hasAttachments) {
        editor.commands.insertContent(withoutImageNodes(content) as Content);
        onTemplateInserted?.();
        toast({
          variant: "subtle",
          status: "warning",
          title: t("Content inserted without images"),
          description: t("This field cannot hold attachments, so the images in the template were left out."),
        });
        setShowTemplateSelector(false);
        return;
      } else {
        editor.commands.insertContent(content);
      }
      onTemplateInserted?.();

      toast({
        variant: "subtle",
        status: "success",
        title: t("Content inserted"),
        description: `${t("Inserted content from the template called")} ${template.name}`,
      });
      setShowTemplateSelector(false);
    },
    [editor, onCopyTemplateDocuments, onTemplateInserted, toast]
  );

  const setLink = useCallback(() => {
    if (!editor) return;

    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run();
      return;
    }

    const protocol = "https://";

    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", !previousUrl ? protocol : previousUrl);

    if (url === null) return;

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url.startsWith(protocol) ? url : protocol + url })
      .run();
  }, [editor]);

  const keyboardHintId = useId();

  const { containerRef, focusCurrent, toolbarHandlers } = useRovingToolbar({
    onEscape: () => editor?.commands.focus(),
  });

  useImperativeHandle(ref, () => ({ focus: focusCurrent }), [focusCurrent]);

  const cursorInTable = useEditorState({
    editor: editor ?? null,
    selector: ({ editor: current }) => !!current && !current.isDestroyed && current.isActive("table"),
  });

  if (!editor) return null;

  const inTable = toolbarConfig.table && cursorInTable === true;
  const showsTemplates = toolbarConfig.template && sub.hasDocumentTemplateAccess;
  const showsAutofill =
    toolbarConfig.autofill && sub.hasAiAccess && sub.allowsAiFeature(AiFeature.TextDraft) && !!onAutofill;
  const showsAskFurther = toolbarConfig.autofill && sub.hasAiAccess && !!onAutofill && !!askFurther && !!editor.getText().trim();

  return (
    <>
      {/* Announced when focus enters the toolbar; the sighted equivalent is EditorKeyboardHint. */}
      <VisuallyHidden id={keyboardHintId}>
        {t("Arrow keys move between the buttons. Escape returns to the text.")}
      </VisuallyHidden>
      <Box
        ref={containerRef}
        role="toolbar"
        aria-label={t("Text formatting") ?? "Text formatting"}
        aria-describedby={keyboardHintId}
        aria-orientation="horizontal"
        width="full"
        px="2"
        py="2"
        {...toolbarHandlers}
      >
        <Flex
          columnGap="2"
          rowGap="1"
          flexWrap="wrap"
          alignItems="center"
          sx={{ "& > [data-toolbar-group] + [data-toolbar-group]": { borderLeft: "1px solid", borderColor: "smBorder", pl: "2" } }}
        >
          {toolbarConfig.undoRedo && (
            <ToolbarGroup>
              <UndoRedoButtons editor={editor} />
            </ToolbarGroup>
          )}
          {(toolbarConfig.bold || toolbarConfig.italic || toolbarConfig.underline) && (
            <ToolbarGroup>
              {toolbarConfig.bold && (
                <ToolbarButton
                  label={t("Bold")}
                  isPressed={editor.isActive("bold")}
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  fontWeight={"bold"}
                >
                  {t("B")}
                </ToolbarButton>
              )}
              {toolbarConfig.italic && (
                <ToolbarButton
                  label={t("Italic")}
                  isPressed={editor.isActive("italic")}
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  fontStyle={"italic"}
                >
                  {t("I")}
                </ToolbarButton>
              )}
              {toolbarConfig.underline && (
                <ToolbarButton
                  label={t("Underline")}
                  isPressed={editor.isActive("underline")}
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  textDecoration={"underline"}
                >
                  {t("U")}
                </ToolbarButton>
              )}
            </ToolbarGroup>
          )}
          {toolbarConfig.headings && (
            <ToolbarGroup>
              <ToolbarButton
                isPressed={isPlainParagraph(editor)}
                onClick={() => editor.chain().focus().clearNodes().setParagraph().run()}
              >
                {t("Normal text")}
              </ToolbarButton>
              <ToolbarButton
                label={t("H1 heading")}
                isPressed={editor.isActive("heading", { level: 1 })}
                onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              >
                {t("H1")}
              </ToolbarButton>
              <ToolbarButton
                label={t("H2 heading")}
                isPressed={editor.isActive("heading", { level: 2 })}
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              >
                {t("H2")}
              </ToolbarButton>
              <ToolbarButton
                label={t("H3 heading")}
                isPressed={editor.isActive("heading", { level: 3 })}
                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              >
                {t("H3")}
              </ToolbarButton>
            </ToolbarGroup>
          )}
          {toolbarConfig.colors && (
            <ToolbarGroup>
              <ColorPalettes editor={editor} />
            </ToolbarGroup>
          )}
          {toolbarConfig.lists && (
            <ToolbarGroup>
              <ToolbarButton
                label={t("Bullet list")}
                isPressed={editor.isActive("bulletList")}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
              >
                <Icon as={FaListUl} />
              </ToolbarButton>
              <ToolbarButton
                label={t("Numbered list")}
                isPressed={editor.isActive("orderedList")}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
              >
                <Icon as={FaListOl} />
              </ToolbarButton>
            </ToolbarGroup>
          )}
          {(toolbarConfig.link || toolbarConfig.table || (toolbarConfig.image && onImageSelect)) && (
            <ToolbarGroup>
              {toolbarConfig.link && (
                <ToolbarButton label={t("Insert link")} isPressed={editor.isActive("link")} onClick={setLink}>
                  <Icon as={FaLink} />
                </ToolbarButton>
              )}
              {toolbarConfig.table && (
                <ToolbarButton
                  label={t("Insert table")}
                  onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                  isDisabled={inTable}
                >
                  <Icon as={FaTable} />
                </ToolbarButton>
              )}
              {toolbarConfig.image && onImageSelect && (
                <>
                  <chakra.input
                    ref={imageInputRef}
                    aria-hidden="true"
                    tabIndex={-1}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    display="none"
                    onChange={handleImageChange}
                  />
                  <ToolbarButton label={t("Insert image")} onClick={triggerImageInput}>
                    <Icon as={FaImage} />
                  </ToolbarButton>
                </>
              )}
            </ToolbarGroup>
          )}
          {(showsTemplates || showsAutofill || showsAskFurther) && (
            <HStack spacing="1" marginLeft="auto">
              {showsTemplates && (
                <TemplatePicker
                  isOpen={showTemplateSelector}
                  onOpen={() => setShowTemplateSelector(true)}
                  onClose={() => setShowTemplateSelector(false)}
                  isLoading={isLoading}
                  isError={templatesFailed}
                  onRetry={refetchTemplates}
                  isInserting={isInsertingTemplate}
                  templates={documentTemplatesData?.documents ?? []}
                  onSelect={handleTemplateSelect}
                />
              )}
              {showsAutofill && (
                <ToolbarButton
                  colorScheme="teal"
                  variant="outline"
                  onClick={onAutofill}
                  isLoading={isOpeningAutofill}
                  leftIcon={<Icon as={FaMagic} />}
                >
                  {t("Autofill")}
                </ToolbarButton>
              )}
              {showsAskFurther && (
                <ToolbarButton
                  colorScheme="teal"
                  variant="ghost"
                  onClick={() =>
                    askFurther({
                      source: askFurtherAbout?.source ?? t("The text"),
                      subject: askFurtherAbout?.subject,
                      text: editor.getText(),
                    })
                  }
                  leftIcon={<Icon as={FiMessageSquare} />}
                >
                  {t("Ask further")}
                </ToolbarButton>
              )}
            </HStack>
          )}
        </Flex>
        {inTable && (
          <Flex
            role="group"
            aria-label={t("Table") ?? "Table"}
            columnGap="1"
            rowGap="1"
            flexWrap="wrap"
            alignItems="center"
            mt="2"
            pt="2"
            borderTop="1px solid"
            borderColor="smBorder"
          >
            <ToolbarButton variant="ghost" onClick={() => editor.chain().focus().addRowAfter().run()}>
              {t("Add row")}
            </ToolbarButton>
            <ToolbarButton variant="ghost" onClick={() => editor.chain().focus().addColumnAfter().run()}>
              {t("Add column")}
            </ToolbarButton>
            <ToolbarButton variant="ghost" onClick={() => editor.chain().focus().deleteRow().run()}>
              {t("Delete row")}
            </ToolbarButton>
            <ToolbarButton variant="ghost" onClick={() => editor.chain().focus().deleteColumn().run()}>
              {t("Delete column")}
            </ToolbarButton>
            <ToolbarButton variant="ghost" colorScheme="red" onClick={() => editor.chain().focus().deleteTable().run()}>
              {t("Delete table")}
            </ToolbarButton>
          </Flex>
        )}
      </Box>
    </>
  );
});

MenuBar.displayName = "MenuBar";

const ToolbarGroup = ({ children }: { children: ReactNode }) => (
  <HStack spacing="1" data-toolbar-group="">
    {children}
  </HStack>
);

type TemplateOption = { label: string; value: string; tags: string[] };

const TemplatePicker = ({
  isOpen,
  onOpen,
  onClose,
  isLoading,
  isError,
  onRetry,
  isInserting,
  templates,
  onSelect,
}: {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  isInserting: boolean;
  templates: TextDocumentDto[];
  onSelect: (template: TextDocumentDto) => void;
}) => {
  const selectScreenReaderMessages = useSelectScreenReaderMessages<TemplateOption>();
  const searchRef = useRef<HTMLInputElement | null>(null);
  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom-end"
      isLazy
      initialFocusRef={searchRef as RefObject<HTMLInputElement>}
    >
      <PopoverTrigger>
        <ToolbarButton
          variant="outline"
          backgroundColor="smBackground"
          borderColor="smBorder"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          leftIcon={<Icon as={FaFileImport} />}
          isLoading={isInserting}
          loadingText={t("Inserting the template …")}
        >
          {t("Insert template")}
        </ToolbarButton>
      </PopoverTrigger>
      <Portal>
        <PopoverContent width="xs" aria-label={t("Insert template") ?? "Insert template"} rootProps={{ zIndex: "popover" }}>
          <PopoverBody p="2">
            {isError ? (
              <Flex direction="column" alignItems="flex-start" gap="2" p="2" role="alert">
                <Text fontSize="sm">{t("Could not load templates.")}</Text>
                <ToolbarButton variant="outline" onClick={onRetry}>
                  {t("Try again")}
                </ToolbarButton>
              </Flex>
            ) : (
              <Select<TemplateOption>
                {...selectScreenReaderMessages}
                ref={(select: SelectInstance<TemplateOption, false, GroupBase<TemplateOption>> | null) => {
                  searchRef.current = select?.inputRef ?? null;
                }}
                openMenuOnFocus
                onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                  if (event.key !== "Escape") return;
                  event.preventDefault();
                  event.stopPropagation();
                  onClose();
                }}
                size="sm"
                placeholder={`${t("Select document template")}...`}
                isLoading={isLoading || isInserting}
                isDisabled={isInserting}
                loadingMessage={() => t("Loading…")}
                noOptionsMessage={({ inputValue }: { inputValue: string }) =>
                  inputValue ? t("No templates match the search") : t("No templates yet")
                }
                options={templates.map((doc) => ({
                  label: doc.name ? doc.name : (t("No title") ?? ""),
                  value: doc.id,
                  tags: doc.tags,
                }))}
                filterOption={(option: { label: string; data: TemplateOption }, inputValue: string) => {
                  const search = inputValue.toLowerCase();
                  return (
                    option.label.toLowerCase().includes(search) ||
                    option.data.tags.some((tag) => tag.toLowerCase().includes(search))
                  );
                }}
                chakraStyles={{
                  menu: (provided: SystemStyleObject) => ({ ...provided, position: "static", boxShadow: "none", my: "1" }),
                  menuList: (provided: SystemStyleObject) => ({ ...provided, maxHeight: "64" }),
                }}
                components={{ Option: CustomOption }}
                onChange={(option: TemplateOption | null) => {
                  const template = templates.find((d) => d.id === option?.value);
                  if (template) onSelect(template);
                }}
              />
            )}
            {isInserting && (
              <Text fontSize="sm" color="smMuted" px="2" pt="1" role="status">
                {t("Inserting the template …")}
              </Text>
            )}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

const CustomOption = (props: OptionProps<{ label: string; value: string; tags: string[] }, false>) => {
  return (
    <components.Option {...props}>
      <Flex alignItems="center" _hover={{ backgroundColor: "smBackgroundSecondary", cursor: "pointer" }} p="2" m="0">
        <Text fontSize="sm" pr="1">
          {props.label}
        </Text>
        <Flex columnGap="1">
          {props.data.tags.map((tag) => (
            <Tag key={tag} size="sm" alignItems="center">
              <TagLabel>{tag}</TagLabel>
            </Tag>
          ))}
        </Flex>
      </Flex>
    </components.Option>
  );
};

const UndoRedoButtons = ({ editor }: { editor: Editor }) => {
  const { canUndo, canRedo } = useEditorState({
    editor,
    selector: ({ editor: current }) =>
      !current || current.isDestroyed
        ? { canUndo: false, canRedo: false }
        : { canUndo: current.can().undo(), canRedo: current.can().redo() },
  });
  return (
    <>
      <ToolbarButton label={t("Undo")} isDisabled={!canUndo} onClick={() => editor.chain().focus().undo().run()}>
        <Icon as={FaUndo} />
      </ToolbarButton>
      <ToolbarButton label={t("Redo")} isDisabled={!canRedo} onClick={() => editor.chain().focus().redo().run()}>
        <Icon as={FaRedo} />
      </ToolbarButton>
    </>
  );
};

const ColorPalettes = ({ editor }: { editor: Editor }) => {
  const { textColor, highlightColor } = useEditorState({
    editor,
    selector: ({ editor: current }): { textColor?: string; highlightColor?: string } =>
      !current || current.isDestroyed
        ? {}
        : {
            textColor: current.getAttributes("textStyle").color ?? undefined,
            highlightColor: current.getAttributes("highlight").color ?? undefined,
          },
  });
  return (
    <>
      <ColorPalette
        label={t("Text colour")}
        swatches={textColorSwatches()}
        current={textColor}
        removeLabel={t("Remove colour")}
        onSelect={(color) => editor.chain().focus().setColor(color).run()}
        onRemove={() => editor.chain().focus().unsetColor().run()}
        trigger={{
          border: "1px solid",
          borderColor: "smBorder",
          backgroundColor: "smBackground",
          color: textColor ?? "inherit",
          rounded: "full",
          p: "0",
        }}
      >
        A
      </ColorPalette>
      <ColorPalette
        label={t("Highlight colour")}
        swatches={highlightColorSwatches()}
        current={highlightColor}
        removeLabel={t("Remove highlight")}
        onSelect={(color) => editor.chain().focus().setHighlight({ color }).run()}
        onRemove={() => editor.chain().focus().unsetHighlight().run()}
        trigger={{
          border: "1px solid",
          borderColor: "smBorder",
          backgroundColor: highlightColor ?? "smBackground",
          rounded: "full",
          cursor: "pointer",
          p: "1",
        }}
      >
        <Icon as={FaHighlighter} />
      </ColorPalette>
    </>
  );
};
